/**
 * Sample behavior data for the sequence-detect page.
 *
 * Generates a deterministic 24h of dog-pose events **client-side** — no DB
 * rows, nothing that can be mistaken for a real recording. The three panels
 * (Behavior Summary, Behavior Frequency, Behavior Timeline V2) all read the
 * same Redux store, so one dispatch fills all of them.
 *
 * The state vocabulary is the model's own: sit / stand / lie / walk /
 * scratch / unsure (behaviorTypes.ts maps these to the UI labels).
 */

import type { DogPoseDataPoint } from '../store/slices/dashboardSlice';

/** Per-hour behavior weights over a day: [sit, stand, lie, walk, scratch, unsure]. */
const HOUR_WEIGHTS: number[][] = [
  [0.05, 0.02, 0.90, 0.02, 0.00, 0.01], // 00 deep night
  [0.05, 0.02, 0.90, 0.02, 0.00, 0.01],
  [0.05, 0.02, 0.90, 0.02, 0.00, 0.01],
  [0.05, 0.02, 0.90, 0.02, 0.00, 0.01],
  [0.08, 0.03, 0.85, 0.03, 0.00, 0.01], // 04
  [0.15, 0.08, 0.65, 0.10, 0.00, 0.02], // 05 dawn stir
  [0.25, 0.20, 0.30, 0.20, 0.02, 0.03], // 06 morning active
  [0.30, 0.25, 0.20, 0.20, 0.03, 0.02], // 07
  [0.25, 0.20, 0.30, 0.20, 0.03, 0.02], // 08
  [0.30, 0.20, 0.30, 0.15, 0.03, 0.02], // 09
  [0.35, 0.15, 0.35, 0.10, 0.02, 0.03], // 10 midday calm
  [0.35, 0.15, 0.35, 0.10, 0.02, 0.03], // 11
  [0.30, 0.15, 0.40, 0.10, 0.02, 0.03], // 12
  [0.30, 0.20, 0.30, 0.15, 0.03, 0.02], // 13
  [0.25, 0.20, 0.35, 0.15, 0.03, 0.02], // 14
  [0.30, 0.20, 0.30, 0.15, 0.03, 0.02], // 15
  [0.25, 0.25, 0.25, 0.20, 0.03, 0.02], // 16 afternoon
  [0.30, 0.25, 0.20, 0.20, 0.03, 0.02], // 17
  [0.25, 0.25, 0.25, 0.20, 0.04, 0.01], // 18 evening walk
  [0.30, 0.20, 0.30, 0.15, 0.03, 0.02], // 19
  [0.35, 0.15, 0.35, 0.10, 0.03, 0.02], // 20
  [0.25, 0.10, 0.55, 0.07, 0.02, 0.01], // 21 settling
  [0.15, 0.05, 0.75, 0.03, 0.00, 0.02], // 22
  [0.08, 0.03, 0.85, 0.02, 0.00, 0.02], // 23 night
];

const STATES = ['sit', 'stand', 'lie', 'walk', 'scratch', 'unsure'];

/** Deterministic LCG so the same seed always draws the same day. */
function makeRng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1103515245 + 12345) % 2147483648;
    return s / 2147483648;
  };
}

/**
 * Generate one pose every 5 minutes over the last `hours` hours (default 24).
 * Deterministic: the same `now` and seed produce the same series.
 */
export function generateSampleDogPoses(now: number = Date.now(), hours = 24): DogPoseDataPoint[] {
  const rng = makeRng(42);
  const HOUR = 3600 * 1000;
  const poses: DogPoseDataPoint[] = [];

  for (let h = 0; h < hours; h++) {
    const weights = HOUR_WEIGHTS[(24 - hours + h) % 24];
    for (let m = 0; m < 60; m += 5) {
      const ts = now - (hours - 1 - h) * HOUR - (59 - m) * 60 * 1000;
      if (ts > now) continue;
      const r = rng();
      let acc = 0;
      let idx = 0;
      for (let i = 0; i < weights.length; i++) {
        acc += weights[i];
        if (r <= acc) { idx = i; break; }
      }
      poses.push({
        id: poses.length + 1,
        sessionId: 'sample',
        deviceName: 'sample',
        timestamp: ts,
        startedAt: ts,
        dogState: STATES[idx],
        bboxX: Math.round(rng() * 400),
        bboxY: Math.round(rng() * 300),
        bboxWidth: 120 + Math.round(rng() * 80),
        bboxHeight: 100 + Math.round(rng() * 60),
        bboxConfidence: 0.75 + rng() * 0.24,
        facingDirection: 'center',
        facingAngle: 0,
        facingConfidence: 0.9,
        isValid: true,
      });
    }
  }
  return poses;
}

/** Distinct states the sample data uses (drives the filter buttons). */
export function sampleDistinctStates(): string[] {
  return [...STATES];
}

/** Frequency counts keyed by dog_state (what dashboard:dogPose:frequency returns). */
export function sampleFrequency(poses: DogPoseDataPoint[]): Record<string, number> {
  const freq: Record<string, number> = {};
  for (const p of poses) {
    const s = p.dogState ?? 'unsure';
    freq[s] = (freq[s] ?? 0) + 1;
  }
  return freq;
}

/** Duration totals in minutes keyed by dog_state (what dogPose:duration returns). */
export function sampleDuration(poses: DogPoseDataPoint[]): Record<string, number> {
  // Each pose stands for a 5-minute slot; count the slots per state.
  const dur: Record<string, number> = {};
  for (const p of poses) {
    const s = p.dogState ?? 'unsure';
    dur[s] = (dur[s] ?? 0) + 5;
  }
  return dur;
}